import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { get, post, put } from '@/lib/api/client';
import type {
  OrderHeader,
  OrderItem,
  OrderLog,
  OrderSearchResult,
  Paged,
} from './types';

/**
 * Order search.
 *
 * The API takes its filters as one space-separated string of tags, e.g.
 * `"ACTIVE TODAY DELIVERY BUSINESS-abc123"`. Building it in one place keeps the
 * stray-whitespace handling — the original re-trimmed after every append, in
 * two separate functions that had to stay in step — out of the components.
 */

export interface OrderFilters {
  status: string;
  time: string;
  type: string;
  payment: string;
  businessId: string;
}

export const EMPTY_FILTERS: OrderFilters = {
  // The original defaulted to DISPATCHED TODAY, which is the useful first view: an
  // unfiltered list is every order ever placed.
  status: 'DISPATCHED TODAY',
  time: '',
  type: '',
  payment: '',
  businessId: '',
};

export function buildFilterString(filters: OrderFilters): string {
  return [
    filters.status,
    filters.time,
    filters.type,
    filters.payment,
    filters.businessId ? `BUSINESS-${filters.businessId}` : '',
  ]
    .filter(Boolean)
    .join(' ');
}

export function useOrderSearch({
  term,
  filters,
  page,
  size,
  sort,
}: {
  term: string;
  filters: OrderFilters;
  page: number;
  size: number;
  sort: 'asc' | 'desc';
}) {
  const filterString = buildFilterString(filters);

  return useQuery({
    queryKey: ['orders', 'search', term, filterString, page, size, sort],
    queryFn: () =>
      post<OrderSearchResult>(`admin/orders/${size}/${page}`, {
        SearchString: term,
        FilterString: filterString,
        OrderAsc: sort === 'asc',
        OrderDesc: sort === 'desc',
      }),
    // Paging should not blank the table back to a skeleton.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Counts for the stat cards. Same filters, minus the free-text term. */
export function useOrderHeader(filters: OrderFilters) {
  const filterString = buildFilterString(filters);

  return useQuery({
    queryKey: ['orders', 'header', filterString],
    queryFn: () =>
      post<OrderHeader>('businesses/orderheader', { filterString }),
    staleTime: 30_000,
  });
}

/** Loaded only when a row is opened. */
export function useOrderItems(orderId: string | null) {
  return useQuery({
    queryKey: ['orders', orderId, 'items'],
    queryFn: () =>
      get<Paged<OrderItem>>(`orders/${orderId}/items/all`).then(
        (page) => page.results
      ),
    enabled: Boolean(orderId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useOrderLogs(orderId: string | null) {
  return useQuery({
    queryKey: ['orders', orderId, 'logs'],
    queryFn: () =>
      get<Paged<OrderLog>>(`orders/${orderId}/logs`).then(
        (page) => page.results
      ),
    enabled: Boolean(orderId),
    staleTime: 5 * 60 * 1000,
  });
}


/**
 * The catch-all order mutation. `PUT orders` takes whatever subset of fields
 * the action needs, always alongside the order id.
 */
export interface OrderUpdate {
  OrderId: string;
  ConfirmationCode?: string | null;
  Status?: number;
  ApproverId?: string;
  Reason?: number;
  PaymentType?: number;
}

export function useUpdateOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update: OrderUpdate) => put<unknown>('orders', update),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/** `PUT orders/servicefee/toggle/{id}` — no body. */
export function useToggleServiceFee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderId: string) =>
      put<unknown>(`orders/servicefee/toggle/${orderId}`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/** Keyed by confirmation code, not id — that is what the endpoint takes. */
export function useForceCapture() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (confirmationCode: string) =>
      post<unknown>(`admin/forcecapture/${confirmationCode}`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

export function useSetDriverInstructions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { OrderId: string; Instructions: string }) =>
      post<unknown>('orders/setdriverinstructions', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

export function useChangeToPickup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderId: string) =>
      put<unknown>(`orders/changetopickup/${orderId}`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}


/**
 * Addresses this customer may take delivery at from this store.
 *
 * Keyed on the *store's* primary location, not the order's — the endpoint is
 * asking "which of this user's addresses can that store reach".
 */
export function useDeliveryLocations({
  customerId,
  businessLocationId,
  enabled,
}: {
  customerId: string | null;
  businessLocationId: string | null;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: ['orders', 'delivery-locations', customerId, businessLocationId],
    queryFn: () =>
      get<Paged<OrderLocationOption>>(
        `orders/getuserlocationsforDelivery/${customerId}/${businessLocationId}`
      ).then((page) => page.results),
    enabled: enabled && Boolean(customerId) && Boolean(businessLocationId),
  });
}

export interface OrderLocationOption {
  id: string;
  name: string | null;
  addressLine: string | null;
  city: string | null;
  zipcode: string | null;
}

export function useChangeToDelivery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      locationId,
    }: {
      orderId: string;
      locationId: string;
    }) => put<unknown>(`orders/changetodelivery/${orderId}/${locationId}`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/**
 * The receipt's item list.
 *
 * Note this is `orders/{id}/items`, not `.../items/all` — the receipt shows
 * what was ordered, split into included and voided.
 */
export function useReceiptItems(orderId: string | null) {
  return useQuery({
    queryKey: ['orders', orderId, 'receipt-items'],
    queryFn: () =>
      get<Paged<OrderItem>>(`orders/${orderId}/items`).then(
        (page) => page.results
      ),
    enabled: Boolean(orderId),
    staleTime: 5 * 60 * 1000,
  });
}

export interface PaymentLog {
  id: string;
  createdAt: string | null;
  operationName: string | null;
  terminalId: string | null;
  /** Raw gateway payload, shown behind a preview toggle. */
  jsonData: string | null;
}

export function useGiftCardBalance() {
  return useMutation({
    mutationFn: (code: string) =>
      get<{ balance?: number; amount?: number }>(`giftcards/balance/${code}`),
  });
}

export function usePaymentLogs(orderId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['orders', orderId, 'payment-logs'],
    queryFn: () =>
      get<Paged<PaymentLog>>(`pos/orders/payment/logs/${orderId}`).then(
        (page) => page.results
      ),
    enabled: enabled && Boolean(orderId),
  });
}
